import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards } from "swiper/modules";
import MuxPlayer from "@mux/mux-player-react";

import "swiper/css";
import "swiper/css/effect-cards";
import "./StackSlider.css";

const StackSlider = ({ data = [] }) => {
  const textSwiperRef = useRef(null);
  const videoSwiperRef = useRef(null);
  const playerRefs = useRef({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingStates, setPlayingStates] = useState({});
  const [mutedStates, setMutedStates] = useState({});

  useEffect(() => {
    const loader = document.getElementById("home-slider-loader");
    if (loader) loader.style.display = "none";
  }, []);

  // Sync sliders
  const handleSlideChange = (swiper) => {
    const newIndex = swiper.activeIndex;
    const oldIndex = activeIndex;
    setActiveIndex(newIndex);

    // Sync both swipers
    if (textSwiperRef.current && textSwiperRef.current !== swiper) {
      textSwiperRef.current.slideTo(newIndex);
    }
    if (videoSwiperRef.current && videoSwiperRef.current !== swiper) {
      videoSwiperRef.current.slideTo(newIndex);
    }

    // Check if previous video was playing
    const wasPreviousPlaying = playingStates[oldIndex];

    // Pause all videos and reset to poster
    Object.keys(playerRefs.current).forEach((key) => {
      const index = parseInt(key);
      const player = playerRefs.current[key];
      if (player?.media?.nativeEl) {
        player.media.nativeEl.pause();
        player.media.nativeEl.currentTime = 0; // Reset to beginning to show poster
        if (index !== newIndex) {
          setPlayingStates((prev) => ({ ...prev, [index]: false }));
        }
      }
    });

    // Only auto-play if previous video was playing
    if (wasPreviousPlaying) {
      const player = playerRefs.current[newIndex];
      if (player?.media?.nativeEl) {
        player.media.nativeEl.muted = false;
        setMutedStates((prev) => ({ ...prev, [newIndex]: false }));
        player.media.nativeEl.play().catch(() => {});
        setPlayingStates((prev) => ({ ...prev, [newIndex]: true }));
      }
    }
  };

  const togglePlayPause = (index) => {
    const player = playerRefs.current[index];
    if (player?.media?.nativeEl) {
      const vid = player.media.nativeEl;
      if (vid.paused) {
        vid.muted = false;
        setMutedStates((prev) => ({ ...prev, [index]: false }));
        vid.play().catch(() => {});
        setPlayingStates((prev) => ({ ...prev, [index]: true }));
      } else {
        vid.pause();
        vid.currentTime = 0; // Reset to beginning to show poster
        setPlayingStates((prev) => ({ ...prev, [index]: false }));
      }
    }
  };

  const toggleMute = (index) => {
    const player = playerRefs.current[index];
    if (player?.media?.nativeEl) {
      const vid = player.media.nativeEl;
      const newMutedState = !vid.muted;
      vid.muted = newMutedState;
      setMutedStates((prev) => ({ ...prev, [index]: newMutedState }));
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="stack-slider-empty">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="stack-slider-container">
      <div className="stack-slider-wrapper">
        {/* Text Slider */}
        <div className="stack-slider-text">
          <div className="slider-label">Traditional Text Job</div>
          <Swiper
            effect="cards"
            grabCursor={true}
            modules={[EffectCards]}
            onSwiper={(swiper) => (textSwiperRef.current = swiper)}
            onSlideChange={handleSlideChange}
            className="text-swiper"
          >
            {data.map((item, index) => (
              <SwiperSlide key={`text-${index}`}>
                <div className="job-card">
                  <div className="job-card-header">
                    <h3 className="job-title">{item.job.title}</h3>
                    <div className="job-meta">
                      <div className="job-company">
                        <i className="feather-briefcase"></i>
                        <span>{item.job.company}</span>
                      </div>
                      <div className="job-location">
                        <i className="feather-map-pin"></i>
                        <span>{item.job.location}</span>
                      </div>
                      {item.job.posted_at && (
                        <div className="job-posted">
                          <i className="feather-clock"></i>
                          <span>
                            Posted{" "}
                            {new Date(item.job.posted_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="job-card-body">
                    <div
                      className="job-description"
                      dangerouslySetInnerHTML={{
                        __html: item.job.description,
                      }}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Arrow */}
        <div className="slider-arrow">
          <i className="feather-arrow-right slider-arrow-desktop"></i>
          <i className="feather-arrow-down slider-arrow-mobile"></i>
        </div>

        {/* Video Slider */}
        <div className="stack-slider-video">
          <div className="slider-label">Our Job Peek</div>
          <Swiper
            effect="cards"
            grabCursor={true}
            modules={[EffectCards]}
            onSwiper={(swiper) => (videoSwiperRef.current = swiper)}
            onSlideChange={handleSlideChange}
            className="stack-video-swiper"
          >
            {data.map((item, index) => (
              <SwiperSlide key={`stack-video-${index}`}>
                <div
                  className="stack-video-card"
                  onClick={() => togglePlayPause(index)}
                >
                  <MuxPlayer
                    ref={(el) => (playerRefs.current[index] = el)}
                    playbackId={item.peek.mux_playback_id}
                    muted
                    playsInline
                    loop
                    className="stack-peek-player"
                    thumbnailTime={0}
                    style={{
                      "--media-object-fit": "cover",
                      "--controls": "none",
                    }}
                  />
                  {!playingStates[index] && (
                    <div className="animated-poster">
                      <img
                        src={`https://mux.jobpeeks.com/${item.peek.mux_playback_id}/animated.gif`}
                        alt="Video preview"
                      />
                    </div>
                  )}
                  {playingStates[index] && (
                    <button
                      className="mute-unmute-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMute(index);
                      }}
                      aria-label={
                        mutedStates[index] ? "Unmute video" : "Mute video"
                      }
                    >
                      <i
                        className={
                          mutedStates[index] !== false ? "feather-volume-x" : "feather-volume-2"
                        }
                      ></i>
                    </button>
                  )}
                  <button
                    className="play-pause-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlayPause(index);
                    }}
                    aria-label={
                      playingStates[index] ? "Pause video" : "Play video"
                    }
                  >
                    <i
                      className={
                        playingStates[index] ? "feather-pause" : "feather-play"
                      }
                    ></i>
                  </button>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Slide Indicator */}
      <div className="slide-indicator">
        {data.map((_, index) => (
          <div
            key={`indicator-${index}`}
            className={`indicator-dot ${index === activeIndex ? "active" : ""}`}
          />
        ))}
      </div>
    </div>
  );
};

export default StackSlider;
